import { SyftFormField } from "../blocks/forms/types";
import { FormInputField, isFormInputElement } from "../common/utils";
import { getLabelText } from "./autotrack/utils";

/**
 * Sensitive input types that should never have their values tracked
 */
const SENSITIVE_TYPES = new Set(["password", "hidden"]);

/**
 * Patterns in field names that indicate sensitive data
 * These fields will have their values redacted
 */
const SENSITIVE_NAME_PATTERNS = [
  "password",
  "passwd",
  "pass",
  "pwd",
  "secret",
  "credit",
  "card",
  "cvv",
  "cvc",
  "ccv",
  "ssn",
  "social",
  "security",
  "pin",
  "token",
  "auth",
  "otp",
];

/**
 * Maximum length for tracked field values to prevent excessive data
 */
const MAX_VALUE_LENGTH = 500;

/**
 * Checks if a field name indicates sensitive data
 */
function isSensitiveFieldName(name: string): boolean {
  const lowerName = name.toLowerCase();
  return SENSITIVE_NAME_PATTERNS.some((pattern) => lowerName.includes(pattern));
}

/**
 * Sanitizes a field value for tracking
 * - Returns "[REDACTED]" for sensitive fields
 * - Truncates long values
 * - Returns empty string for empty values
 */
function sanitizeValue(value: string, type: string, name: string): string {
  // Redact sensitive field types
  if (SENSITIVE_TYPES.has(type)) {
    return "[REDACTED]";
  }

  // Redact fields with sensitive names
  if (isSensitiveFieldName(name)) {
    return "[REDACTED]";
  }

  // Truncate long values
  if (value.length > MAX_VALUE_LENGTH) {
    return value.substring(0, MAX_VALUE_LENGTH) + "...";
  }

  return value;
}

/**
 * Extracts form fields from a form element
 * Filters out sensitive data for security
 */
export function getFormFields(form: HTMLFormElement): SyftFormField[] {
  const fields: SyftFormField[] = [];
  const children = Array.from(form.elements);

  children.forEach((child) => {
    if (!isFormInputElement(child)) return;

    // Cast to the appropriate type
    const inputElement = child as
      | HTMLInputElement
      | HTMLSelectElement
      | HTMLTextAreaElement;

    // Skip empty values (use === "" since value is always a string)
    // Skip unchecked checkboxes/radios
    if (
      inputElement.value === "" ||
      (inputElement instanceof HTMLInputElement &&
        (inputElement.type === "checkbox" || inputElement.type === "radio") &&
        !inputElement.checked)
    )
      return;

    // Skip file inputs - value contains local path, not useful and potentially sensitive
    if (
      inputElement instanceof HTMLInputElement &&
      inputElement.type === "file"
    ) {
      return;
    }

    const label = getLabelTextForInput(inputElement as FormInputField);
    const sanitizedValue = sanitizeValue(
      inputElement.value,
      inputElement.type,
      inputElement.name
    );

    fields.push({
      id: inputElement.id,
      label,
      name: inputElement.name,
      type: inputElement.type,
      tagName: inputElement.tagName,
      value: sanitizedValue,
    });
  });

  return fields;
}

/**
 * Gets label text for an input field
 */
function getLabelTextForInput(input: FormInputField): string | undefined {
  if (input.labels != null && input.labels.length > 0) {
    const labels = Array.from(input.labels)
      .map((g) => getLabelText(g))
      .filter((label) => label != null && label.length > 0);
    if (labels.length > 0) {
      return labels[0];
    }
  }
  return undefined;
}
